import {
  FilterToolbar,
  FormContent,
  PageCanvas,
} from "@/components/page-canvas";
import { Form, Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import AppLayout from "@/layouts/app_layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import CaseWorkspace, { CaseFacts } from "@/components/case-workspace";
import { FormErrors, errorText } from "@/components/forms/form-errors";
import {
  locationVehiclePath,
  locationVehicleOwnershipPath,
  newLocationVehicleOwnershipPath,
} from "@/routes";
import type { VehicleOwner, VehicleProfile } from "./types";

export default function VehicleOwnership({
  vehicle,
  customers,
  query,
  pagination,
}: {
  vehicle: VehicleProfile;
  customers: VehicleOwner[];
  query: string;
  pagination: { page: number; pages: number };
}) {
  const { auth, errors } = usePage<{ errors: Record<string, string> }>().props;
  const { t } = useTranslation();
  if (!auth.location) return null;
  const locationId = auth.location.id;
  const searchPath = newLocationVehicleOwnershipPath(locationId, vehicle.id);
  return (
    <AppLayout
      breadcrumbs={[
        {
          title: `${vehicle.make} ${vehicle.model}`,
          href: locationVehiclePath(locationId, vehicle.id),
        },
      ]}
    >
      <Head title={t("vehicles.transfer")} />
      <PageCanvas>
        <CaseWorkspace
          context={
            <CaseFacts
              facts={[
                {
                  label: t("vehicles.details"),
                  value: `${vehicle.make} ${vehicle.model}`,
                },
                {
                  label: t("vehicles.registration_number"),
                  value:
                    vehicle.registration_number || t("customers.not_supplied"),
                },
                {
                  label: t("resource_workspace.location"),
                  value: auth.location.name,
                },
              ]}
            />
          }
        >
          <FormContent>
            <h1 className="text-page font-display">{t("vehicles.transfer")}</h1>
            <p className="text-muted-foreground text-sm">
              {t("vehicles.transfer_help")}
            </p>
            <FormErrors
              errors={errors || {}}
              targets={{
                customer_id: "transfer-customer",
                reason: "transfer-reason",
              }}
            />
            <FilterToolbar action={searchPath} method="get" columns="search">
              <div className="space-y-2">
                <Label htmlFor="owner-search">
                  {t("vehicles.search_owner")}
                </Label>
                <Input id="owner-search" name="q" defaultValue={query} />
              </div>
              <Button
                type="submit"
                variant="outline"
                className="justify-self-start"
              >
                {t("vehicles.search")}
              </Button>
            </FilterToolbar>

            <Form
              key={vehicle.revision}
              action={locationVehicleOwnershipPath(locationId, vehicle.id)}
              method="post"
            >
              {({ processing }) => (
                <div className="space-y-4">
                  <input
                    type="hidden"
                    name="revision"
                    value={vehicle.revision}
                  />
                  <div className="space-y-2">
                    <Label htmlFor="transfer-customer">
                      {t("vehicles.new_owner")}
                    </Label>
                    <NativeSelect
                      id="transfer-customer"
                      name="customer_id"
                      defaultValue=""
                      required
                      className="w-full"
                      aria-invalid={!!errors?.customer_id}
                      aria-describedby={
                        errors?.customer_id
                          ? "transfer-customer-error"
                          : undefined
                      }
                    >
                      <option value="" disabled>
                        {t("vehicles.choose_owner")}
                      </option>
                      {customers.map((customer) => (
                        <option key={customer.id} value={customer.id}>
                          {customer.name}
                        </option>
                      ))}
                    </NativeSelect>
                    {errors?.customer_id && (
                      <p
                        id="transfer-customer-error"
                        className="text-sm text-destructive"
                      >
                        {errorText(errors.customer_id)}
                      </p>
                    )}
                  </div>
                  {customers.length === 0 && (
                    <p className="text-muted-foreground text-sm">
                      {t("vehicles.no_owner")}
                    </p>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="transfer-reason">
                      {t("vehicles.reason")}
                    </Label>
                    <Input
                      id="transfer-reason"
                      name="reason"
                      aria-invalid={!!errors?.reason}
                      aria-describedby={
                        errors?.reason ? "transfer-reason-error" : undefined
                      }
                    />
                    {errors?.reason && (
                      <p
                        id="transfer-reason-error"
                        className="text-sm text-destructive"
                      >
                        {errorText(errors.reason)}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      type="submit"
                      disabled={processing || customers.length === 0}
                    >
                      {t("vehicles.confirm_transfer")}
                    </Button>
                    <Button
                      variant="outline"
                      render={
                        <Link
                          href={locationVehiclePath(locationId, vehicle.id)}
                        />
                      }
                    >
                      {t("customers.cancel")}
                    </Button>
                  </div>
                </div>
              )}
            </Form>

            <nav
              aria-label={t("customers.pages")}
              className="flex flex-wrap items-center gap-3"
            >
              {pagination.page > 1 && (
                <Link
                  className="underline"
                  href={newLocationVehicleOwnershipPath(
                    locationId,
                    vehicle.id,
                    {
                      page: pagination.page - 1,
                      q: query,
                    },
                  )}
                >
                  {t("customers.previous")}
                </Link>
              )}
              <span className="text-muted-foreground text-sm">
                {t("customers.page", pagination)}
              </span>
              {pagination.page < pagination.pages && (
                <Link
                  className="underline"
                  href={newLocationVehicleOwnershipPath(
                    locationId,
                    vehicle.id,
                    {
                      page: pagination.page + 1,
                      q: query,
                    },
                  )}
                >
                  {t("customers.next")}
                </Link>
              )}
            </nav>
          </FormContent>
        </CaseWorkspace>
      </PageCanvas>
    </AppLayout>
  );
}
